import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import {
  ArrowRightIcon,
  BuildingIcon,
  LightbulbIcon,
  MailIcon,
  PercentIcon,
  UserPlusIcon,
  UsersIcon,
} from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import type { Invitation } from "simplydo/interfaces";
import { ButtonLink } from "simplydo/react/ui/button";
import { CardContent } from "simplydo/react/ui/card";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";

import api from "api";

import { HomeRow, HomeRowContainer, HomeRowIcon } from "../../HomeUI";
import HomeBlock from "../HomeBlock";
import InvitationBlockRow from "./InvitationBlockRow";

const invitationIconByForType: Record<Invitation["forType"], typeof MailIcon> =
  {
    group: UsersIcon,
    requestJoinGroup: UsersIcon,
    idea: LightbulbIcon,
    requestJoinIdea: LightbulbIcon,
    ideaAssessor: PercentIcon,
    projectInvited: UserPlusIcon,
    ideaBusinessProfile: BuildingIcon,
  };

const InvitationsBlock = () => {
  const { t } = useTranslation();

  const dashboardQuery = useQuery(initUseQuery(api.users.dashboard, []));

  const invitations = useMemo(
    () => dashboardQuery.data?.invitations ?? [],
    [dashboardQuery.data?.invitations],
  );

  const acceptableInvitations = useMemo(
    () =>
      invitations.filter(
        (invitation) =>
          !invitation.createdByCurrentUser && invitation.canAccept,
      ),
    [invitations],
  );

  if (!acceptableInvitations.length) return null;

  const countPerRow = 3;
  const columns = acceptableInvitations.length > countPerRow ? 2 : 1;
  const visibleInvitations = acceptableInvitations.slice(
    0,
    countPerRow * columns,
  );

  return (
    <HomeBlock $width={columns}>
      <CardContent>
        <Label color="green" size="small">
          To review
        </Label>
        <H3>
          Pending invitations
          <Subheader>
            {acceptableInvitations.length}{" "}
            {acceptableInvitations.length === 1
              ? t("generic.invitation")
              : t("generic.invitations")}{" "}
            waiting for your response.
          </Subheader>
        </H3>

        <HomeRowContainer $columns={columns}>
          {visibleInvitations.map((invitation) => {
            const icon =
              invitationIconByForType[invitation.forType] ?? MailIcon;
            return (
              <HomeRow key={invitation._id}>
                <HomeRowIcon>
                  <Icon icon={icon} />
                </HomeRowIcon>

                <InvitationBlockRow invitation={invitation} />
              </HomeRow>
            );
          })}
        </HomeRowContainer>
      </CardContent>
      <CardContent color="primary" textAlign="right">
        <ButtonLink
          color="primary"
          variant="ghost"
          size="small"
          to="/invitations"
        >
          View all {t("generic.invitations")}
          <Icon icon={ArrowRightIcon} />
        </ButtonLink>
      </CardContent>
    </HomeBlock>
  );
};

export default InvitationsBlock;
